import store from '@/store';

import { isClientCabin } from '@/utils/helpers';

import type { NavigationGuardNext, Route, RouteConfig } from 'vue-router';

function beforeEnter(_to: Route, _from: Route, next: NavigationGuardNext) {
    if (isClientCabin()) {
        next();
    } else {
        next('/');
    }
}

async function beforeEnterVIP(
    _to: Route,
    _from: Route,
    next: NavigationGuardNext
) {
    if (isClientCabin()) {
        await store.dispatch('user/authorize');

        if (store.getters['user/hasActiveSession']) {
            if (store.getters['user/isVIPReseller']) {
                next();
            } else {
                next('/404');
            }
        } else {
            // non-authorized users are allowed to access
            next();
        }
    } else {
        next('/');
    }
}

const routes: RouteConfig[] = [
    {
        path: '/info',
        name: 'ClientCabinInfo',
        component: () => import('@/pages/ClientCabin/Info'),
        meta: { title: 'Grow Your Business', configurable: true },
        beforeEnter
    },
    {
        path: '/about',
        name: 'ClientCabinAbout',
        component: () => import('@/pages/ClientCabin/About'),
        meta: { title: 'About Us' },
        beforeEnter
    },
    {
        path: '/book',
        name: 'ClientCabinBook',
        component: () => import('@/pages/ClientCabin/Book'),
        meta: { title: 'Book Appointment', configurable: true },
        beforeEnter
    },
    {
        path: '/proposal',
        name: 'ClientCabinProposal',
        component: () => import('@/pages/ClientCabin/Proposal'),
        meta: { title: 'Grow Your Business', configurable: true },
        beforeEnter
    },
    {
        path: '/examples',
        name: 'ClientCabinExamples',
        component: () => import('@/pages/ClientCabin/Examples'),
        meta: { title: 'Examples' },
        beforeEnter
    },
    {
        path: '/onboarding',
        name: 'ClientCabinOnBoarding',
        component: () => import('@/pages/ClientCabin/OnBoarding'),
        meta: { title: 'Welcome!' },
        beforeEnter
    },
    {
        path: '/reasons',
        name: 'ClientCabinReasons',
        component: () => import('@/pages/ClientCabin/Reasons'),
        meta: { title: 'Results & Reasons' },
        beforeEnter
    },
    {
        path: '/reasons/megaphone-effect',
        name: 'ClientCabinReasonsR1',
        component: () => import('@/pages/ClientCabin/Reasons/BoostYourBrand'),
        meta: { title: 'Megaphone Effect' },
        beforeEnter
    },
    {
        path: '/reasons/paid-advertising-vs-content-marketing',
        name: 'ClientCabinReasonsR2',
        component: () => import('@/pages/ClientCabin/Reasons/ContentMarketing'),
        meta: { title: 'Paid Advertising vs. Content Marketing' },
        beforeEnter
    },
    {
        path: '/reasons/techcrunch-wired-article-coverage-cost-to-submit-is-it-worth-it',
        name: 'ClientCabinReasonsR3',
        component: () => import('@/pages/ClientCabin/Reasons/CostToSubmit'),
        meta: {
            title: 'Forbes, TechCrunch & Wired Article Coverage | Cost to Submit & Is it Worth It?'
        },
        beforeEnter
    },
    {
        path: '/reasons/zero-traffic-keywords-have-huge-search-volume',
        name: 'ClientCabinReasonsR4',
        component: () =>
            import('@/pages/ClientCabin/Reasons/ZeroTrafficKeywords'),
        meta: {
            title: 'Zero Traffic Keywords Have Huge Search Volume'
        },
        beforeEnter
    },
    {
        path: '/reasons/theres-no-such-thing-as-bad-publicity',
        name: 'ClientCabinReasonsR5',
        component: () => import('@/pages/ClientCabin/Reasons/BadPublicity'),
        meta: {
            title: 'There’s No Such Thing As Bad Publicity: Why Brands Work Better When They Tell Their Own Stories'
        },
        beforeEnter
    },
    {
        path: '/reasons/keyword-tools-are-keeping-you-poor-stop-using-them',
        name: 'ClientCabinReasonsR6',
        component: () => import('@/pages/ClientCabin/Reasons/KeywordTools'),
        meta: {
            title: 'Keyword Tools Are Keeping You Poor, Stop Using Them'
        },
        beforeEnter
    },
    {
        path: '/reasons/why-e-commerce-stores-fail',
        name: 'ClientCabinReasonsR7',
        component: () => import('@/pages/ClientCabin/Reasons/ECommerceStores'),
        meta: {
            title: 'Why E-commerce Stores Fail: How To Not Become Yet Another Shopify Flop'
        },
        beforeEnter
    },
    {
        path: '/reasons/citations-google-maps-rankings',
        name: 'ClientCabinReasonsR8',
        component: () =>
            import('@/pages/ClientCabin/Reasons/GoogleMapsRankings'),
        meta: {
            title: 'How To Increase Your Google Maps Rankings'
        },
        beforeEnter
    },
    {
        path: '/reasons/successful-wine-marketing-example',
        name: 'ClientCabinStoryS1',
        component: () => import('@/pages/ClientCabin/Reasons/WineMarketing'),
        meta: { title: 'Successful Wine Marketing Example' },
        beforeEnter
    },
    {
        path: '/reasons/online-medical-store',
        name: 'ClientCabinStoriesS2',
        component: () =>
            import('@/pages/ClientCabin/Reasons/OnlineMedicalStore'),
        meta: { title: 'Online Medical Store Generates Millions in Sales' },
        beforeEnter
    },
    {
        path: '/reasons/car-dealership-marketing-strategy-example',
        name: 'ClientCabinStoriesS3',
        component: () => import('@/pages/ClientCabin/Reasons/CarDealership'),
        meta: { title: 'Car Dealership Marketing Strategy Example' },
        beforeEnter
    },
    {
        path: '/reasons/content-amplification-strategy',
        name: 'ClientCabinStoriesS4',
        component: () =>
            import('@/pages/ClientCabin/Reasons/ContentAmplificationStrategy'),
        meta: {
            title: 'Using Content Amplification Strategies To Drive Leads & Conversions For NYC Commercial Spaces'
        },
        beforeEnter
    },
    {
        path: '/reasons/roofing-company-on-page-1-of-google-in-3-months',
        name: 'ClientCabinStoriesS5',
        component: () => import('@/pages/ClientCabin/Reasons/RoofingCompany'),
        meta: {
            title: 'Roofing Company Marketing Strategy & Branding Plan Puts Texas Contractor On Page 1 Of Google in 3 Months'
        },
        beforeEnter
    },
    {
        path: '/reasons/almost-fired',
        name: 'ClientCabinStoriesS7',
        component: () => import('@/pages/ClientCabin/Reasons/AlmostFired'),
        meta: {
            title: 'We almost got fired: 14,700 clicks at 10x ROAS!'
        },
        beforeEnter
    },
    {
        path: '/reasons/how-to-submit-a-press-release-to-google-news',
        name: 'ClientCabinStoriesS8',
        component: () => import('@/pages/ClientCabin/Reasons/GoogleNews'),
        meta: {
            title: 'How to Submit a Press Release to Google News'
        },
        beforeEnter
    },
    {
        path: '/reasons/the-buyers-journey',
        name: 'ClientCabinStoriesS9',
        component: () => import('@/pages/ClientCabin/Reasons/BuyersJourney'),
        meta: {
            title: 'Use The Buyer’s Journey 4 Stages to Get More Traffic & Sales'
        },
        beforeEnter
    },
    {
        path: '/reasons/blog-post-traffic-volume-calculator-graph',
        name: 'ClientCabinR10',
        component: () =>
            import('@/pages/ClientCabin/Reasons/TrafficCalculator'),
        meta: {
            title: 'Blog Post Traffic & Volume Calculator & Graph'
        },
        beforeEnter
    },
    {
        path: '/thanks/:product?',
        name: 'ClientCabinThanks',
        component: () => import('@/pages/ClientCabin/Thanks'),
        props: true,
        meta: { title: 'Thanks!' },
        beforeEnter
    },
    {
        path: '/premium',
        name: 'ClientCabinPremium',
        component: () => import('@/pages/ClientCabin/Premium'),
        meta: { title: 'The Big 6' },
        beforeEnter
    },
    {
        path: '/defi-distribution',
        name: 'ClientCabinDeFi',
        component: () => import('@/pages/ClientCabin/DeFi'),
        meta: { title: 'DeFi Distribution' },
        beforeEnter
    },
    {
        path: '/blow',
        name: 'ClientCabinBLOW',
        component: () => import('@/pages/ClientCabin/BLOW'),
        meta: { title: 'Blog Link Outreach Wire (B.L.O.W.)' },
        beforeEnter
    },
    {
        path: '/msn',
        name: 'ClientCabinMsn',
        component: () => import('@/pages/ClientCabin/MSN'),
        meta: { title: 'MSN Distribution' },
        beforeEnter
    },
    {
        path: '/leads-from-ads',
        name: 'ClientCabinLeadsFromAds',
        component: () => import('@/pages/ClientCabin/LeadsFromAds'),
        meta: { title: 'Leads From Ads', configurable: true },
        beforeEnter: beforeEnterVIP
    },
    {
        path: '/custom-pricing',
        name: 'ClientCabinCustomPricing',
        component: () => import('@/pages/ClientCabin/CustomPricing'),
        meta: { title: 'Custom Pricing' },
        beforeEnter
    }
];

export default {
    routes,
    index: routes[0].path
};
